import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';

export const HERITAGE_PLAQUE_OPEN_EVENT = 'panoee:heritage-plaque:open';

export const openHeritagePlaque = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(HERITAGE_PLAQUE_OPEN_EVENT));
};

const foldVi = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase();

const HERITAGE_PATTERN =
  /bang di tich|di tich quoc gia|thang long tu tran|modeltest|model tes|model test|heritage plaque/;

export const isHeritagePlaqueHotspot = (hotspot?: {
  id?: string;
  title?: string;
  type?: string;
  polygon?: boolean;
  post?: { title?: string };
  config?: Record<string, any>;
}) => {
  if (!hotspot) return false;
  if (hotspot.id === '6a82b7b50d6d53332e2711be') return true;
  if (hotspot.polygon && hotspot.type === 'image') return true;
  const haystack = foldVi(
    [
      hotspot.id,
      hotspot.title,
      hotspot.post?.title,
      hotspot.config?.article?.title,
      hotspot.config?.callout?.text,
      hotspot.config?.style?.callout?.text,
      hotspot.config?.media?.media?.name,
      hotspot.config?.image?.galleries?.[0]?.name,
    ]
      .filter(Boolean)
      .join(' '),
  );
  return HERITAGE_PATTERN.test(haystack);
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20240;
  background: rgba(18, 12, 8, 0.55);
  pointer-events: auto;
`;

const Frame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const HeritagePlaqueHost = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(HERITAGE_PLAQUE_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(HERITAGE_PLAQUE_OPEN_EVENT, onOpen);
  }, []);
  return <HeritagePlaqueModal open={open} onClose={() => setOpen(false)} />;
};

const HeritagePlaqueModal = ({ open, onClose }: Props) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'heritage-plaque-close') onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMessage);
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <Backdrop role="presentation">
      <Frame
        title="Bảng di tích quốc gia đặc biệt"
        src="/static/heritage/index.html"
        allow="fullscreen"
      />
    </Backdrop>
  );
};

export default memo(HeritagePlaqueModal);
